import Swiper from 'swiper/bundle';
import type { Swiper as SwiperInstance, SwiperOptions } from 'swiper/types';
import { el, removeAttributes, throttle } from "./utils";

export type GalleryItem = {
    hasContent: boolean;
    src: string;
    srcLarge: string | null;
    srcSmall: string | null;
    tags: string[],
    title: string;
    caption: string;
    el: HTMLElement;
    mediaType: string;
    media: Element;
    portrait: boolean;
    height: number;
};

type SliderOptions = SwiperOptions & {
    startingIndex?: number;
    autoplayVideos?: boolean;
    autoPauseVideos?: boolean;
}

type GalleryColumn = {
    el: HTMLElement;
    height: number;
}

class MCLGallerySlider {
    private items!: GalleryItem[];
    private parent!: HTMLElement;
    private slider!: HTMLElement;
    sliderInstance!: SwiperInstance;
    private nextButton!: HTMLButtonElement;
    private prevButton!: HTMLButtonElement;

    private options: SliderOptions;

    constructor(items: GalleryItem[], options: SliderOptions) {
        this.items = items;
        this.options = {
            startingIndex: 0,
            autoplay: false,
            autoHeight: true,
            loop: true,
            autoplayVideos: false,
            autoPauseVideos: true,
            ...options,
        }

        this.build();
    }

    private build = (): void => {
        this.slider = el('div', { class: 'mcl-gallery-popup__slider swiper' });
        const sliderTrack = el('div', { class: 'mcl-gallery-popup__slider-track swiper-wrapper' });

        this.nextButton = el('button', {
            class: 'mcl-gallery-popup__slider-btn next',
            html: `<svg fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" /></svg>`
        });

        this.prevButton = el('button', {
            class: 'mcl-gallery-popup__slider-btn prev',
            html: `<svg fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" /></svg>`
        });

        for (let item of this.items) {
            sliderTrack.appendChild(this.createGalleryItem(item, true));
        }

        this.slider.append(sliderTrack, this.prevButton, this.nextButton);
    }

    private createGalleryItem = (item: GalleryItem, lazy: boolean = false): HTMLElement => {
        const galleryItem = el('div', { class: 'mcl-gallery-popup__slide swiper-slide' });
        const mediaWrapper = el('div', { class: 'mcl-gallery-popup__slide-media' });
        const content = el('div', { class: 'mcl-gallery-popup__slide-content' });
        let mediaElement;

        switch (item.mediaType) {
            case 'IMG':
                mediaWrapper.classList.add('media-image');
                mediaElement = el<HTMLImageElement>('img', {
                    src: lazy ? '' : item.srcLarge as string,
                    'data-src': item.srcLarge as string
                });
                if (item.portrait) {
                    mediaWrapper.style.backgroundImage = `url(${item.srcSmall})`
                    mediaWrapper.classList.add('portrait');
                }
                mediaWrapper.appendChild(mediaElement);
                break;
            case 'VIDEO':
                mediaElement = el<HTMLVideoElement>('video', {
                    src: lazy ? '' : item.src,
                    'data-src': item.src,
                });

                mediaElement.autoplay = true;
                mediaElement.controls = true;
                mediaElement.muted = true;

                mediaWrapper.appendChild(mediaElement);
                break;
        }

        galleryItem.appendChild(mediaWrapper);

        if (item.hasContent) {
            if (item.title) content.appendChild(el('h3', { text: item.title }));
            if (item.caption) content.appendChild(el('p', { text: item.caption }));
            galleryItem.appendChild(content);
        }

        return galleryItem;
    }

    init = () => {
        this.sliderInstance = new Swiper(this.slider, {
            autoplay: this.options.autoplay,
            loop: this.options.loop,
            rewind: this.options.rewind,
            autoHeight: this.options.autoHeight,
            navigation: {
                nextEl: this.nextButton,
                prevEl: this.prevButton,
            },

            on: {
                init: this.onInit,
                click: this.onClick,
                realIndexChange: this.onRealIndexChange,
                activeIndexChange: this.onSlideChange,
            }
        });
    }

    onInit = (swiper: SwiperInstance) => {
        swiper.slideTo(this.options.startingIndex as number);
        this.preloadSlidesMedia(swiper);
    }

    onClick = (swiper: SwiperInstance) => {
        swiper.slideNext();
    }

    onRealIndexChange = (swiper: SwiperInstance) => {
        this.preloadSlidesMedia(swiper);
    }

    preloadSlidesMedia = (swiper: SwiperInstance) => {
        const total = swiper.slides.length;
        let offsets;
        if (total < 3) {
            offsets = [];
            for (let i = -Math.floor(total / 2); i <= Math.floor(total / 2); i++) {
                offsets.push(i);
            }
        } else {
            offsets = [-1, 0, 1];
        }

        offsets.forEach((offset) => {
            const index = (swiper.realIndex + offset + swiper.slides.length) % swiper.slides.length;
            const slide = swiper.slides[index];
            if (slide) {
                const media = slide.querySelector('[data-src]') as HTMLElement;
                if (media) {
                    media.setAttribute('src', media.dataset.src as string);
                    media.removeAttribute('data-src');
                }
            }
        });
    }

    onSlideChange = (swiper: SwiperInstance) => {
        const prevSlide = swiper.slides[swiper.previousIndex];
        const currentSlide = swiper.slides[swiper.realIndex];

        this.toggleVideo(currentSlide, prevSlide);
    }

    appendTo = (parent: HTMLElement): void => {
        this.parent = parent;
        this.parent.appendChild(this.slider);
    }

    destroy = (): void => {
        this.sliderInstance.destroy();
        this.parent.removeChild(this.slider);
    }

    private toggleVideo = (currentSlide: HTMLElement | undefined, prevSlide: HTMLElement | undefined) => {
        const prevVideo = prevSlide?.querySelector<HTMLVideoElement>('video');
        if (prevVideo) {
            prevVideo.pause();
            prevVideo.currentTime = 0;
            prevVideo.muted = true;
            prevVideo.volume = 0;
        }

        const currentVideo = currentSlide?.querySelector<HTMLVideoElement>('video');
        if (currentVideo) {
            currentVideo.play();
            currentVideo.muted = false;
            currentVideo.volume = 0.3;
        }
    }
}

class MCLGallery {
    private gallery: HTMLElement;
    private galleryStyle: string = 'grid';
    private colsCount: number = 1;
    private isMasonry: boolean = false;
    private columns: GalleryColumn[] = [];
    private items: GalleryItem[];
    private itemsWrapper!: HTMLElement;
    private tagsWrapper!: HTMLElement;
    private tagsPos: string;
    private activeTag!: string;
    private filtered: GalleryItem[] = [];
    private itemsTimeout?: number;

    private popup!: HTMLElement;
    private popupBody!: HTMLElement;
    private slider!: MCLGallerySlider;
    private popupTimeout?: number;

    private classNames = {
        galleryItems: 'mcl-gallery__items',
        galleryItem: 'mcl-gallery__item',
        galleryCol: 'mcl-gallery__col',
        tagsWrapper: 'mcl-gallery__tags',
        tag: 'mcl-gallery__tag',

        popup: 'mcl-gallery-popup',
    }

    constructor(gallery: HTMLElement) {
        this.gallery = gallery;
        this.galleryStyle = this.gallery.dataset.style || 'grid';
        this.tagsPos = this.gallery.dataset.tagsPos || 'center';
        this.colsCount = Number(this.gallery.dataset.cols) || 1;

        this.items = this.prepItems(Array.from(this.gallery.querySelectorAll(':scope > *')));
        this.filtered = [...this.items];

        this.isMasonry = this.galleryStyle === 'masonry' && this.colsCount > 1;

        this.initGallery();
        this.initPopup();

        this.events();
    }

    private events = (): void => {
        this.tagsWrapper.addEventListener('click', this.switchTag);
        this.itemsWrapper.addEventListener('click', this.openPopup);
        window.addEventListener('resize', throttle(this.handleResize))
    }

    private initGallery = (): void => {
        this.buildGallery();
        this.renderTags();
        this.renderItems();
        this.handleResize();
    }

    private buildGallery = (): void => {
        this.gallery.innerHTML = '';
        this.tagsWrapper = el('div', { class: `${this.classNames.tagsWrapper} ${this.tagsPos}` });
        this.itemsWrapper = el('div', { class: this.classNames.galleryItems });
        this.gallery.style.setProperty('--cols', this.gallery.dataset.cols || '3');

        if (this.isMasonry) {
            this.createCols(this.colsCount);
        }

        this.gallery.append(this.tagsWrapper, this.itemsWrapper);
    }

    private createCols = (count: number) => {
        this.itemsWrapper.innerHTML = '';
        this.columns = [];

        for (let i = 0; i < count; i++) {
            const col = el('div', { class: this.classNames.galleryCol });
            this.columns.push({
                el: col,
                height: 0
            });
        }

        this.itemsWrapper.append(...this.columns.map(col => col.el));
    }

    private handleResize = () => {
        if (!this.isMasonry) return;

        let cols = this.colsCount;

        if (this.colsCount >= 4) {
            if (window.innerWidth < 768) {
                cols = 2;
            } else if (window.innerWidth < 960) {
                cols = 3;
            }
        } else if (this.colsCount === 3) {
            if (window.innerWidth < 768) {
                cols = 2;
            }
        }

        this.createCols(cols);
        this.gallery.style.setProperty('--cols', cols.toString());

        this.renderItems();
    }

    private prepItems(items: Element[]): GalleryItem[] {
        const itemsArray: GalleryItem[] = [];

        for (let i = 0; i < items.length; i++) {
            const item = el('div', { class: this.classNames.galleryItem });
            item.style.setProperty('--cols', items[i].getAttribute('data-cols') || '1');
            item.style.setProperty('--rows', items[i].getAttribute('data-rows') || '1');

            const mediaHeight = 100 * (items[i].clientHeight / items[i].clientWidth);

            item.appendChild(items[i]);

            const title = items[i].getAttribute('data-title') || '';
            const caption = items[i].getAttribute('data-caption') || '';
            const tags = items[i].getAttribute('data-tags')?.split(',') || [];

            const src = items[i].getAttribute('data-lazy-src') || items[i].getAttribute('src');

            itemsArray.push({
                hasContent: !!title || !!caption,
                src: src as string,
                srcLarge: items[i].getAttribute('data-src-lg') || null,
                srcSmall: items[i].getAttribute('data-src-sm') || null,
                tags,
                title,
                caption,
                el: item,
                mediaType: items[i].tagName,
                media: items[i],
                portrait: items[i].hasAttribute('data-portrait'),
                height: mediaHeight
            });

            removeAttributes(items[i], [
                'data-tags',
                'data-caption',
                'data-cta-url',
                'data-cta-text',
                'data-cols',
                'data-rows',
                'data-style',
                'data-portrait',
            ]);
        }
        return itemsArray;
    }

    private renderItems = (): void => {
        clearTimeout(this.itemsTimeout);

        this.filtered.forEach(item => {
            item.el.classList.add('hide');
            const video = item.el.querySelector('video');
            if (video) video.pause();
        });

        this.itemsTimeout = setTimeout(() => {
            if (this.isMasonry) {
                this.columns.forEach(col => {
                    col.el.innerHTML = '';
                    col.height = 0;
                });
            } else {
                this.itemsWrapper.innerHTML = '';
            }

            this.filtered.forEach(item => item.el.classList.remove('hide'));

            this.filtered = (!this.activeTag || this.activeTag === 'all')
                ? [...this.items]
                : this.items.filter(item => item.tags.includes(this.activeTag));

            for (let i = 0; i < this.filtered.length; i++) {
                const item = this.filtered[i].el;
                item.classList.add('show');
                item.setAttribute('data-index', i.toString());

                if (this.isMasonry) {
                    let column = this.columns[i % this.columns.length];
                    // if (column.height > 0) {
                    //     column = this.columns.reduce((a, b) => (a.height < b.height ? a : b));
                    // }

                    column.el.appendChild(item);
                    column.height += this.filtered[i].height;
                } else {
                    this.itemsWrapper.appendChild(item);
                }

                if (this.filtered[i].mediaType === 'VIDEO') {
                    const video = item.querySelector('video');
                    video?.setAttribute('playsinline', '')
                    video?.setAttribute('webkit-playsinline', '')
                    if (video) video.play();
                }
            }
        }, 300);
    }

    private renderTags = (): void => {
        if (!this.gallery.dataset.tags) {
            this.gallery.removeChild(this.tagsWrapper);
            return;
        }

        const galleryTags = this.gallery.dataset.tags.split(',').map(t => t.trim());
        const tags = ['All', ...galleryTags];

        tags.forEach((tag, i) => {
            const tagButton = el('button', {
                class: `mcl-gallery__tag${i === 0 ? ' active' : ''}`,
                text: tag,
                'data-tag': tag.toLocaleLowerCase(),
            });

            if (i === 0) this.activeTag = tag.toLocaleLowerCase();

            this.tagsWrapper.appendChild(tagButton);
        });
    }

    private switchTag = (e: Event): void => {
        const target = e.target as HTMLElement;
        if (!target.hasAttribute('data-tag') || target.dataset.tag === this.activeTag) return;

        this.tagsWrapper.querySelector(`[data-tag="${this.activeTag}"]`)?.classList.remove('active');
        target.classList.add('active');
        this.activeTag = target.dataset.tag!;
        this.renderItems();
    }

    private initPopup = (): void => {
        this.popup = el('div', { class: 'mcl-gallery-popup' });
        const wrapper = el('div', { class: 'mcl-gallery-popup__wrapper' });
        const overlay = el('div', { class: 'mcl-gallery-popup__overlay' });
        this.popupBody = el('div', { class: 'mcl-gallery-popup__body' });
        const closeButton = el('button', {
            class: 'mcl-gallery-popup__close',
            html: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x-lg" viewBox="0 0 16 16"><path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/></svg>'
        });

        this.popupBody.append(closeButton);
        wrapper.append(overlay, this.popupBody);
        this.popup.appendChild(wrapper);
        this.gallery.appendChild(this.popup);

        closeButton.addEventListener('click', this.closePopup);
        overlay.addEventListener('click', this.closePopup);
    }

    private openPopup = (e: Event): void => {
        const target = e.target as HTMLElement;
        if (!target.classList.contains('mcl-gallery__item')) return;
        document.documentElement.classList.add('overflow-hidden');
        this.popup.classList.add('mcl-gallery-popup--show');
        this.onPopupOpen(target);
    }

    private onPopupOpen = (target: HTMLElement): void => {
        this.slider = new MCLGallerySlider(this.filtered, {
            startingIndex: Number(target.dataset.index)
        });

        this.slider.appendTo(this.popupBody);
        this.slider.init();
    }

    private closePopup = (e: Event): void => {
        e.preventDefault();
        clearTimeout(this.popupTimeout);
        this.popup.classList.add('mcl-gallery-popup--hide');

        this.popupTimeout = window.setTimeout(() => {
            document.documentElement.classList.remove('overflow-hidden');
            this.popup.classList.remove('mcl-gallery-popup--hide', 'mcl-gallery-popup--show');
            this.onPopupClose();
        }, 400);
    }

    private onPopupClose = () => {
        this.slider.destroy();
    }
}

export const mclGallery = () => {
    const galleries = document.querySelectorAll('.mcl-gallery') as NodeListOf<HTMLElement>;
    galleries.forEach(gallery => new MCLGallery(gallery));
}